import queryString from 'query-string';
import remoteServices from '~/constants/RemoteServices';
import * as StreamlinedMobileConstants from '~/constants/StreamlinedMobileConstants';
import {getViewImage} from '~/utils/catalog';

export const sanitizeUrl = unfriendly => {
  if (!unfriendly) return '';
  return (
    unfriendly
      .trim()
      .toLowerCase()
      .replace(/&reg;/, '®')
      .replace(/\/\\/g, '-')
      .replace(/[^-+a-z0-9_\s]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/[-]+/g, '-')
      // remove leading and trailing -
      .replace(/^-/, '')
      .replace(/-$/, '')
  );
};

export const colorProofImageUrl = (color, designId, designLastUpdated) => {
  let imageUrl = remoteServices.proofImages;
  const {alt_uri, uri} = getViewImage(color, 'front');
  if (alt_uri) {
    imageUrl = `${imageUrl}${alt_uri}`;
  } else {
    imageUrl = `${imageUrl}${uri}`;
  }
  const url = buildUrl(imageUrl, {
    design: designId,
    digest: designLastUpdated
  });
  return url;
};

export const proofImageUrl = (
  styleId,
  designId,
  designLastUpdated,
  imgSize = 'front_medium_extended'
) => {
  let imageUrl = remoteServices.proofImages;
  imageUrl = `${imageUrl}/images/catalog/colors/${styleId}/views/alt/${imgSize}.png`;
  const url = buildUrl(imageUrl, {
    design: designId,
    digest: designLastUpdated
  });
  return url;
};

export const getDesignOnFrontProductViewUrl = ({
  alt = true,
  autoNegate = 0,
  backgroundColor = '#F4F4F4',
  designCID,
  designDigest,
  dr = 1,
  imageFormat = 'jpeg',
  imageWidth = 270,
  ixq = 60,
  placeMax = 1,
  placeMaxPct = 0.9,
  placeUseProduct = 1,
  placeUseView = 'front',
  productId,
  imembroider = 0,
  pbbtn = 0
}) => {
  const params = new URLSearchParams();
  params.append('design', designCID);
  params.append('digest', designDigest);
  params.append('autoNegate', autoNegate);
  params.append('dr', dr);
  params.append('ixbg', backgroundColor);
  params.append('ixfm', imageFormat);
  params.append('ixq', ixq);
  params.append('ixw', imageWidth);

  if (imembroider) params.append('imembroider', imembroider);
  if (placeMax) params.append('placeMax', placeMax);
  if (placeMaxPct) params.append('placeMaxPct', placeMaxPct);
  if (placeUseProduct) params.append('placeUseProduct', placeUseProduct);
  if (placeUseView) params.append('placeUseView', placeUseView);

  params.append('pbbtn', pbbtn);

  return `${
    remoteServices.productImages
  }/images/catalog/colors/${productId}/views/${
    alt ? 'alt/' : ''
  }front_medium.png?${params.toString()}`;
};

export function getCatalogImageUrl({
  anchorCatalogDetailImageMD5,
  styleId,
  ixq = 60,
  ixw = 700
}) {
  if (!anchorCatalogDetailImageMD5 || !styleId) return null;

  const params = new URLSearchParams();
  params.append('ixq', ixq);
  params.append('ixw', ixw);

  return `${
    remoteServices.productImages
  }/images/catalog/${anchorCatalogDetailImageMD5}/styles/${styleId}/catalog_detail_image_large.jpg?${params.toString()}`;
}

export function getUrlQuery() {
  return queryString.parse(location.search) || {};
}

export function buildQuery(params, prefix, delimiter) {
  const str = [];
  let p;
  for (p in params) {
    if (Object.prototype.hasOwnProperty.call(params, p)) {
      const e = encodeURIComponent(p);
      const k = prefix ? `${prefix}[${e}]` : e;
      const v = params[p];
      if (v !== null && v !== undefined) {
        if (Array.isArray(v) && delimiter) {
          str.push(`${k}=${encodeURIComponent(v.join(delimiter))}`);
        } else {
          str.push(
            typeof v === 'object'
              ? buildQuery(v, k)
              : `${k}=${encodeURIComponent(v)}`
          );
        }
      }
    }
  }
  return str.join('&');
}

export function buildUrl(baseUrl, options, prefix, delimiter) {
  const q = buildQuery(options, prefix, delimiter);
  if (q.length) {
    return `${baseUrl}?${q}`;
  } else {
    return baseUrl;
  }
}

export function showProductImages() {
  // const query = getUrlQuery();
  // return query.useProductImages === 'true';
  return true;
}

export function getCompositeId() {
  const query = getUrlQuery();
  return query.cid;
}

export function getProductKeys() {
  const query = getUrlQuery();
  const {SK, PK} = query;
  const parsedStyleId = parseInt(SK, 10).toString();
  const parsedColorId = parseInt(PK, 10).toString();
  const inferredStyleId = getInferredStyleIdFromColorId(parsedColorId);
  const validStyle = !isNaN(parsedStyleId);
  const validColor = !isNaN(parsedColorId);
  const validInferredStyle = !isNaN(inferredStyleId);
  return {
    styleId: validStyle
      ? parsedStyleId
      : validInferredStyle
        ? inferredStyleId
        : null,
    colorId: validColor ? parsedColorId : validStyle ? parsedStyleId : null
  };
}

// The rules that determine the styleId and colorId are:
// * The colorId and styleId are strings that can be converted into an integer and have the maximum of 10 digits
// * The colorId for the first color, usually white, is the same value as the styleId
// * The colorId for the first color ends in 00
export function getInferredStyleIdFromColorId(colorId) {
  const parsedColorId = parseInt(colorId, 10);
  if (isNaN(parsedColorId)) return null;
  const colorIdString = parsedColorId.toString();
  return `${colorIdString.substring(0, colorIdString.length - 2)}00`;
}

export function getUserEmail() {
  const query = getUrlQuery();
  return query.E;
}

export function getDesignName() {
  const query = getUrlQuery();
  return query.F;
}

export function getUpload() {
  const query = getUrlQuery();
  return query.U;
}

export function getCampaignLink() {
  const query = getUrlQuery();
  return query.booster;
}

export function getMode() {
  const query = getUrlQuery();
  return query.mode && query.mode.toLowerCase();
}

export function preferSingles() {
  const params = getUrlQuery();
  return params.prefer_singles === 'true';
}

export function getCartMode() {
  const query = getUrlQuery();
  return query.cart === 'true';
}

export function getCartToken() {
  const query = getUrlQuery();
  return query.cart_token;
}

export function getLabLoadMethod() {
  const query = getUrlQuery();
  return query.load_method && query.load_method.toLowerCase();
}

export function isEditDesignInCartMode() {
  return getCartMode() && getLabLoadMethod() === 'editfromcart';
}

export function getCampaignId() {
  const query = getUrlQuery();
  return query.campaign_id && parseInt(query.campaign_id, 10);
}

export function getAuthToken() {
  const query = getUrlQuery();
  return query.token;
}

export function getInternalUserParam() {
  const query = getUrlQuery();
  return query.S && query.S.toLowerCase() === 'internal';
}

export function getSelectIntoTestParam() {
  const query = getUrlQuery();
  const selectInto = query.selectInto;

  if (!selectInto) {
    return {};
  }

  const obj = {};
  selectInto.split(',').forEach(feature => {
    const [flag, group] = feature.split(':');
    obj[flag] = group;
  });
  return obj;
}

export function getExternalUserParam() {
  const query = getUrlQuery();
  return query.S && query.S.toLowerCase() === 'external';
}

export function updateCompositeIdInUrl(cid, regexAppend) {
  const query = getUrlQuery();
  query.cid = cid;
  pushToHistory(`?${queryString.stringify(query)}#`, regexAppend);
}

export function resetLocationSearch(
  options = {},
  route = '',
  setLocation = setLocationSearch
) {
  const params = buildQuery(options);
  pushToHistory(`?${params}#${route}`);
  setLocation(params);
}

function setLocationSearch(params) {
  window.location.search = params;
}

function pushToHistory(string, regexAppend = '') {
  const queryRegex = new RegExp(
    `\\??${location.search.replace('?', '')}#${regexAppend}`
  );
  history.pushState({}, null, location.href.replace(queryRegex, string));
}

export function removeCid() {
  history.pushState({}, null, location.href.replace(location.search, ''));
}

export function currentColorUrlOverride(currentColorId) {
  const {colorId, styleId} = getProductKeys();
  return colorId && styleId ? colorId : currentColorId;
}

export function currentStyleUrlOverride(currentStyleId) {
  const {colorId, styleId} = getProductKeys();
  return colorId && styleId ? styleId : currentStyleId;
}

export function selectionsUrlOverride(selections) {
  const {colorId, styleId} = getProductKeys();
  const hasProductKeys = !!colorId && !!styleId;
  return hasProductKeys ? [{colorId, styleId}] : selections;
}

export function quantitiesUrlOverride(quantities) {
  const {colorId} = getProductKeys();
  return colorId && !quantities[colorId] ? {[colorId]: null} : quantities;
}

export function designParamsPresent() {
  const {cid, email, designName} = getDesignParams();
  return Boolean(cid || (email && designName));
}

export function productParamsPresent() {
  const {colorId, styleId} = getProductKeys();
  return !!colorId || !!styleId;
}

export function uploadParamsPresent() {
  const upload = getUploadParams();
  return !!upload;
}

export function quantityParamsPresent() {
  const quantities = getQuantitiesParams();

  return (
    !!quantities.YXS ||
    !!quantities.YS ||
    !!quantities.YM ||
    !!quantities.YL ||
    !!quantities.YXL ||
    !!quantities.S ||
    !!quantities.M ||
    !!quantities.L ||
    !!quantities.XL ||
    !!quantities.XXL ||
    !!quantities.XXXL ||
    !!quantities.XXXXL ||
    !!quantities['One Size']
  );
}

export function getQuantitiesParams() {
  return getQuantityQueryParams();
}

export function sharedDesignParamsPresent() {
  const query = getUrlQuery();
  return query.SD === 'true';
}

export function getDesignParams() {
  return {
    cid: getCompositeId(),
    email: getUserEmail(),
    designName: getDesignName()
  };
}

export function getUploadParams() {
  const query = getUrlQuery();
  if (query.U) {
    return {
      referenceId: '/uploads/' + encodeURIComponent(query.U)
    };
  }
  return;
}

export function getQuantityQueryParams() {
  const query = getUrlQuery();

  return {
    YXS: query.QYXS ? parseInt(query.QYXS, 10) : 0,
    YS: query.QYS ? parseInt(query.QYS, 10) : 0,
    YM: query.QYM ? parseInt(query.QYM, 10) : 0,
    YL: query.QYL ? parseInt(query.QYL, 10) : 0,
    YXL: query.QYXL ? parseInt(query.QYXL, 10) : 0,
    S: query.QS ? parseInt(query.QS, 10) : 0,
    M: query.QM ? parseInt(query.QM, 10) : 0,
    L: query.QL ? parseInt(query.QL, 10) : 0,
    XL: query.QXL ? parseInt(query.QXL, 10) : 0,
    XXL: query.QXXL ? parseInt(query.QXXL, 10) : 0,
    XXXL: query.QXXXL ? parseInt(query.QXXXL, 10) : 0,
    XXXXL: query.QXXXXL ? parseInt(query.QXXXXL, 10) : 0,
    ['One Size']: query.OneSize ? parseInt(query.OneSize, 10) : 0
  };
}

export function catalogPresent() {
  const {catalog} = getUrlQuery();
  return catalog === 'true';
}

export function orderOptionsPresent() {
  const {TW} = getUrlQuery();
  return TW === 'true';
}

export function buyNowPresent() {
  const {BN} = getUrlQuery();
  return BN === 'true';
}

export function openUploadPresent() {
  const {OU} = getUrlQuery();
  return OU === 'true';
}

export function addComplementaryStylesPresent() {
  const {CS} = getUrlQuery();
  return CS === 'true';
}

export function keywordPresent() {
  const {KW} = getUrlQuery();
  return Boolean(KW);
}

export function getKeyword() {
  const {KW} = getUrlQuery();
  return KW;
}

export function addArtPresent() {
  const {MC} = getUrlQuery();
  return MC === 'true';
}

export function rearViewPresent() {
  const {R} = getUrlQuery();
  return R === '1';
}

export function euParamPresent() {
  const {EU} = getUrlQuery();
  return EU === 'true';
}

export function fundraisingOriginParam() {
  const {FRO} = getUrlQuery();
  return FRO;
}

export function showProductColorPresent() {
  const {SM1} = getUrlQuery();
  return SM1 === 'true';
}

export function getStreamlinedMobileParam() {
  const {SMT} = getUrlQuery();
  const allowedModes = [
    StreamlinedMobileConstants.INTENT_BUSINESS,
    StreamlinedMobileConstants.INTENT_GROUP,
    StreamlinedMobileConstants.INTENT_MYSELF,
    StreamlinedMobileConstants.INTENT_FUNDRAISING
  ];
  return allowedModes.includes(SMT) ? SMT : null;
}
